import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { handleSupabaseError } from "@/lib/errorHandler";
import {
  PollSettings,
  PollType,
  RankingOption,
  PollResultView,
  defaultResultViews,
  normalizePollType,
} from "../pollTypes";

interface Cluster {
  id: string;
  label: string;
  description: string;
  image_url?: string;
  order: number;
  subCategories: SubCategory[];
}

interface SubCategory {
  id: string;
  label: string;
  description: string;
  order: number;
}

interface UsePollRendererDataProps {
  postId: string;
  userId?: string;
  isAdmin: boolean;
  setClusterAllocations: Dispatch<SetStateAction<Record<string, number>>>;
  setSubAllocations: Dispatch<SetStateAction<Record<string, number>>>;
  setRankingOrder: Dispatch<SetStateAction<string[]>>;
  setHideZeroAllocated: Dispatch<SetStateAction<boolean>>;
  setMinimumRequiredRanks: Dispatch<SetStateAction<number>>;
  setHasSubmitted: Dispatch<SetStateAction<boolean>>;
  setShowResults: Dispatch<SetStateAction<boolean>>;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
  setIsAuthModalOpen: Dispatch<SetStateAction<boolean>>;
  setUpdatingResultView: Dispatch<SetStateAction<boolean>>;
}

interface SubmitResponsePayload {
  clusterAllocations: Record<string, number>;
  subAllocations: Record<string, number>;
  rankingOrder: string[];
  minimumRequiredRanks: number;
}

export const usePollRendererData = ({
  postId,
  userId,
  isAdmin,
  setClusterAllocations,
  setSubAllocations,
  setRankingOrder,
  setHideZeroAllocated,
  setMinimumRequiredRanks,
  setHasSubmitted,
  setShowResults,
  setSubmitting,
  setIsAuthModalOpen,
  setUpdatingResultView,
}: UsePollRendererDataProps) => {
  const [pollId, setPollId] = useState<string | null>(null);
  const [pollType, setPollType] = useState<PollType>("constant_sum_two_level");
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [rankingOptions, setRankingOptions] = useState<RankingOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<PollSettings | null>(null);
  const [resultViews, setResultViews] = useState<Record<PollType, PollResultView>>({ ...defaultResultViews });
  const fetchExistingSubmission = useCallback(
    async (currentPollId: string, resolvedType?: PollType) => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      setHasSubmitted(false);
      setShowResults(false);

      if (!authUser?.id || !currentPollId) {
        return false;
      }

      const { data: existingResponse } = await supabase
        .from("poll_responses")
        .select("id")
        .eq("poll_id", currentPollId)
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (!existingResponse) {
        return false;
      }

      const { data: allocations } = await supabase
        .from("poll_allocations")
        .select("*")
        .eq("response_id", existingResponse.id);

      if (!allocations) {
        return false;
      }

      const pollTypeToUse = resolvedType || pollType;

      if (pollTypeToUse === "constant_sum_two_level") {
        const clusterAlloc: Record<string, number> = {};
        const subAlloc: Record<string, number> = {};

        allocations.forEach((alloc) => {
          if (alloc.sub_category_id) {
            subAlloc[alloc.sub_category_id] = alloc.points;
          } else {
            clusterAlloc[alloc.cluster_id] = alloc.points;
          }
        });

        setClusterAllocations(clusterAlloc);
        setSubAllocations(subAlloc);
      } else {
        const typedAllocations = allocations as Array<{ cluster_id: string; points: number; is_ranked?: boolean }>;
        const rankingAllocations =
          pollTypeToUse === "ranking_minimum"
            ? typedAllocations.filter((alloc) => alloc.is_ranked !== false)
            : typedAllocations;

        const sorted = rankingAllocations
          .slice()
          .sort((a, b) => a.points - b.points)
          .map((alloc) => alloc.cluster_id)
          .filter(Boolean) as string[];
        setRankingOrder(sorted);
      }

      setHasSubmitted(true);
      setShowResults(true);

      return true;
    },
    [pollType, setClusterAllocations, setHasSubmitted, setRankingOrder, setShowResults, setSubAllocations],
  );

  const setResultView = useCallback(
    async (view: PollResultView) => {
      const mergedViews = { ...resultViews, [pollType]: view };
      setResultViews(mergedViews);

      if (!pollId || !isAdmin) return;

      setUpdatingResultView(true);
      try {
        const updatedSettings: PollSettings = {
          ...(settings || {}),
          resultViews: mergedViews,
        };
        setSettings(updatedSettings);

        await supabase
          .from("polls")
          .update({
            settings: updatedSettings as unknown as import("@/integrations/supabase/types").Json,
          })
          .eq("id", pollId);

        toast.success("Results view updated");
      } catch (error) {
        console.error("Error updating results view:", error);
        handleSupabaseError(error, "Failed to update results view");
      } finally {
        setUpdatingResultView(false);
      }
    },
    [isAdmin, pollId, pollType, resultViews, setUpdatingResultView, settings],
  );

  const loadPoll = useCallback(async () => {
    try {
      const { data: poll } = await supabase.from("polls").select("*").eq("blog_post_id", postId).maybeSingle();

      if (!poll) {
        setLoading(false);
        return;
      }

      setPollId(poll.id);
      const resolvedType = normalizePollType(poll.poll_type);
      setPollType(resolvedType);
      const pollSettings = poll.settings as PollSettings | null;
      setSettings(pollSettings || {});
      setHideZeroAllocated(pollSettings?.hideZeroAllocated || false);
      setMinimumRequiredRanks(pollSettings?.minimumRequiredRanks ?? 3);
      setResultViews({ ...defaultResultViews, ...(pollSettings?.resultViews || {}) });

      const { data: clustersData } = await supabase
        .from("poll_clusters")
        .select("*")
        .eq("poll_id", poll.id)
        .order("display_order");

      if (clustersData) {
        if (resolvedType === "constant_sum_two_level") {
          const clustersWithSubs = await Promise.all(
            clustersData.map(async (cluster) => {
              const { data: subs } = await supabase
                .from("poll_sub_categories")
                .select("*")
                .eq("cluster_id", cluster.id)
                .order("display_order");

              return {
                id: cluster.id,
                label: cluster.label,
                description: cluster.description || "",
                image_url: cluster.image_url || undefined,
                order: cluster.display_order,
                subCategories:
                  subs?.map((sub) => ({
                    id: sub.id,
                    label: sub.label,
                    description: sub.description || "",
                    order: sub.display_order,
                  })) || [],
              };
            }),
          );
          setClusters(clustersWithSubs);

          if (clustersWithSubs.length > 0) {
            const evenSplit = Math.floor(100 / clustersWithSubs.length);
            const remainder = 100 - evenSplit * clustersWithSubs.length;
            const initialCluster: Record<string, number> = {};
            const initialSubs: Record<string, number> = {};

            clustersWithSubs.forEach((cluster, index) => {
              const clusterValue = evenSplit + (index === 0 ? remainder : 0);
              initialCluster[cluster.id] = clusterValue;

              if (cluster.subCategories.length === 0) {
                return;
              }

              const evenSubSplit = clusterValue === 0 ? 0 : Math.floor(clusterValue / cluster.subCategories.length);
              const subRemainder =
                clusterValue === 0 ? 0 : clusterValue - evenSubSplit * cluster.subCategories.length;

              cluster.subCategories.forEach((sub, subIndex) => {
                const allocation = clusterValue === 0 ? 0 : evenSubSplit + (subIndex < subRemainder ? 1 : 0);
                initialSubs[sub.id] = allocation;
              });
            });

            setClusterAllocations(initialCluster);
            setSubAllocations(initialSubs);
          }
        } else {
          const options = clustersData
            .map((cluster) => ({
              id: cluster.id,
              label: cluster.label,
              description: cluster.description || "",
              image_url: cluster.image_url || undefined,
              order: cluster.display_order,
            }))
            .sort((a, b) => a.order - b.order);

          setRankingOptions(options);
          if (resolvedType === "ranking_minimum") {
            const cappedMinimum = Math.min(pollSettings?.minimumRequiredRanks ?? 3, Math.max(1, options.length));
            setMinimumRequiredRanks(cappedMinimum);
            setRankingOrder((prev) => (prev.length > 0 ? prev : []));
          } else {
            setRankingOrder(options.map((opt) => String(opt.id)).filter(Boolean));
          }
        }
      }

      setLoading(false);

      await fetchExistingSubmission(poll.id, resolvedType);
    } catch (error) {
      console.error("Error loading poll:", error);
      setLoading(false);
    }
  }, [
    fetchExistingSubmission,
    postId,
    setClusterAllocations,
    setHideZeroAllocated,
    setMinimumRequiredRanks,
    setRankingOrder,
    setSubAllocations,
  ]);

  useEffect(() => {
    loadPoll();
  }, [loadPoll, postId, pollType, userId]);

  const submitResponse = useCallback(
    async ({ clusterAllocations, subAllocations, rankingOrder, minimumRequiredRanks }: SubmitResponsePayload) => {
      if (!pollId) return;

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        toast.error("Please sign in to submit your response");
        setIsAuthModalOpen(true);
        return;
      }

      if (pollType === "ranking") {
        const orderToUse =
          rankingOrder.length === rankingOptions.length && rankingOrder.length > 0
            ? rankingOrder
            : rankingOptions.map((option) => String(option.id)).filter(Boolean);

        if (orderToUse.length !== rankingOptions.length) {
          toast.error("Please rank every option before submitting");
          return;
        }

        setSubmitting(true);
        try {
          const allocationsData = orderToUse.map((clusterId, index) => ({
            cluster_id: clusterId,
            sub_category_id: null,
            points: index + 1,
            is_ranked: true,
          }));

          const { error } = await supabase.rpc("submit_poll_response", {
            p_poll_id: pollId,
            p_user_id: authUser.id,
            p_allocations: allocationsData,
          });

          if (error) {
            if (error.message?.includes("already submitted")) {
              toast.error("You have already submitted a response to this poll.");
              setHasSubmitted(true);
              setShowResults(true);
              return;
            }
            throw error;
          }

          setHasSubmitted(true);
          setShowResults(true);
          toast.success("Your ranking has been recorded!");
        } catch (error) {
          console.error("Error submitting poll:", error);
          handleSupabaseError(error, "Failed to submit response");
        } finally {
          setSubmitting(false);
        }

        return;
      }

      if (pollType === "ranking_minimum") {
        const orderToUse = rankingOrder.filter((id) =>
          rankingOptions.some((option) => String(option.id) === String(id)),
        );
        const required = Math.min(minimumRequiredRanks, rankingOptions.length);

        if (orderToUse.length < required) {
          toast.error(`Please rank at least ${required} option${required === 1 ? "" : "s"} before submitting`);
          return;
        }

        setSubmitting(true);
        try {
          const totalOptions = rankingOptions.length;
          const rankedCount = orderToUse.length;
          const remainingCount = Math.max(0, totalOptions - rankedCount);
          const fallbackRank =
            remainingCount > 0 ? (rankedCount + 1 + totalOptions) / 2 : Math.max(rankedCount, totalOptions, 1);

          const allocationsData = [
            ...orderToUse.map((clusterId, index) => ({
              cluster_id: clusterId,
              sub_category_id: null as string | null,
              points: index + 1,
              is_ranked: Boolean(true),
            })),
            ...rankingOptions
              .map((option) => String(option.id))
              .filter((id) => !orderToUse.includes(id))
              .map((clusterId) => ({
                cluster_id: clusterId,
                sub_category_id: null as string | null,
                points: fallbackRank,
                is_ranked: Boolean(false),
              })),
          ];

          const { error } = await supabase.rpc("submit_poll_response", {
            p_poll_id: pollId,
            p_user_id: authUser.id,
            p_allocations: allocationsData,
          });

          if (error) {
            if (error.message?.includes("already submitted")) {
              toast.error("You have already submitted a response to this poll.");
              setHasSubmitted(true);
              setShowResults(true);
              return;
            }
            throw error;
          }

          setHasSubmitted(true);
          setShowResults(true);
          toast.success("Your ranking has been recorded!");
        } catch (error) {
          console.error("Error submitting poll:", error);
          handleSupabaseError(error, "Failed to submit response");
        } finally {
          setSubmitting(false);
        }

        return;
      }

      setSubmitting(true);
      try {
        const allocationsData = [
          ...Object.entries(clusterAllocations).map(([clusterId, points]) => ({
            cluster_id: clusterId,
            sub_category_id: null,
            points,
            is_ranked: true,
          })),
          ...Object.entries(subAllocations).map(([subId, points]) => {
            const cluster = clusters.find((c) => c.subCategories.some((s) => s.id === subId));
            return {
              cluster_id: cluster?.id || null,
              sub_category_id: subId,
              points,
              is_ranked: true,
            };
          }),
        ];

        const { error } = await supabase.rpc("submit_poll_response", {
          p_poll_id: pollId,
          p_user_id: authUser.id,
          p_allocations: allocationsData,
        });

        if (error) {
          if (error.message?.includes("already submitted")) {
            toast.error("You have already submitted a response to this poll.");
            setHasSubmitted(true);
            setShowResults(true);
            return;
          }
          throw error;
        }

        setHasSubmitted(true);
        setShowResults(true);
        toast.success("Your response has been recorded!");
      } catch (error) {
        console.error("Error submitting poll:", error);
        handleSupabaseError(error, "Failed to submit response");
      } finally {
        setSubmitting(false);
      }
    },
    [
      clusters,
      pollId,
      pollType,
      rankingOptions,
      setHasSubmitted,
      setIsAuthModalOpen,
      setShowResults,
      setSubmitting,
    ],
  );

  return {
    pollId,
    pollType,
    clusters,
    rankingOptions,
    settings,
    resultViews,
    loading,
    loadPoll,
    submitResponse,
    setResultView,
    fetchExistingSubmission,
  };
};
